import * as Sentry from '@sentry/react';
import { applyMiddleware, combineReducers, compose, legacy_createStore as createStore } from 'redux';
import { thunk } from 'redux-thunk';

import announcementResponses from '../reducers/announcementResponses';
import announcements from '../reducers/announcements';
import appState from '../reducers/appState';
import attachments from '../reducers/attachments';
import chatMessages from '../reducers/chatMessages';
import chats from '../reducers/chats';
import comments from '../reducers/comments';
import courseLessons from '../reducers/courseLessons';
import courseSections from '../reducers/courseSections';
import faqs from '../reducers/faqs';
import forumPosts from '../reducers/forumPosts';
import forumReplies from '../reducers/forumReplies';
import messages from '../reducers/messages';
import notifications from '../reducers/notifications';
import onlineCourseActivityShares from '../reducers/onlineCourseActivityShares';
import onlineCourseAnnouncements from '../reducers/onlineCourseAnnouncements';
import onlineCourseFeedbacks from '../reducers/onlineCourseFeedbacks';
import onlineCoursePolls from '../reducers/onlineCoursePolls';
import onlineCourseQuestions from '../reducers/onlineCourseQuestions';
import onlineCourses from '../reducers/onlineCourses';
import onlineCourseSurveys from '../reducers/onlineCourseSurveys';
import onlineCourseUsers from '../reducers/onlineCourseUsers';
import platform from '../reducers/platform';
import quizQuestions from '../reducers/quizQuestions';
import quizzes from '../reducers/quizzes';
import reports from '../reducers/reports';
import resourceReactions from '../reducers/resourceReactions';
import resourceViews from '../reducers/resourceViews';
import search from '../reducers/search';
import siteTours from '../reducers/siteTours';
import standardApiRequests, { combinedReducerEnhancer } from '../reducers/standardApiRequests';
import studyRoomErrors from '../reducers/studyRoomErrors';
import studyRooms from '../reducers/studyRooms';
import tutorAvailabilities from '../reducers/tutorAvailabilities';
import tutorFeedbacks from '../reducers/tutorFeedbacks';
import tutorRequests from '../reducers/tutorRequests';
import users from '../reducers/users';
import votes from '../reducers/votes';
import whiteboards from '../reducers/whiteboards';
import workShiftUsers from '../reducers/workShiftUsers';

const reducer = combineReducers({
  apiRequests: standardApiRequests,

  activityBookmarks: standardApiRequests,
  announcementResponses,
  announcements,
  appState,
  attachments,
  blogPosts: standardApiRequests,
  chatMessages,
  chats,
  classRepCourseReports: standardApiRequests,
  classRepCourses: standardApiRequests,
  comments,
  contestEntries: standardApiRequests,
  contractorInfos: standardApiRequests,
  courseAssessments: standardApiRequests,
  courseAssessmentUserNotes: standardApiRequests,
  courseAssignments: standardApiRequests,
  courseAssignmentUsers: standardApiRequests,
  courseInstructors: standardApiRequests,
  courseLessons,
  courseNotesPackages: standardApiRequests,
  courses: standardApiRequests,
  courseScheduleEvents: standardApiRequests,
  coachingSessions: standardApiRequests,
  coachingSessionTemplates: standardApiRequests,
  courseSchedules: standardApiRequests,
  courseSections,
  curriculumSources: standardApiRequests,
  customProducts: standardApiRequests,
  customProductUsers: standardApiRequests,
  discountCodes: standardApiRequests,
  educationHistories: standardApiRequests,
  ejEssays: standardApiRequests,
  ejTemplates: standardApiRequests,
  emails: standardApiRequests,
  examReports: standardApiRequests,
  examReportTemplates: standardApiRequests,
  faqs,
  flashCardDecks: standardApiRequests,
  flashCards: standardApiRequests,
  flashCardUsers: standardApiRequests,
  forumPosts,
  forumReplies,
  invoices: standardApiRequests,
  jobApplicantNotes: standardApiRequests,
  jobApplicants: standardApiRequests,
  jobPositions: standardApiRequests,
  llmChatArtifacts: standardApiRequests,
  llmChats: standardApiRequests,
  llmMessages: standardApiRequests,
  masterySkillEdges: standardApiRequests,
  masterySkills: standardApiRequests,
  messages,
  notifications,
  onlineCourseActivities: standardApiRequests,
  onlineCourseActivityFeedbacks: standardApiRequests,
  onlineCourseActivityLinks: standardApiRequests,
  onlineCourseActivityShares,
  onlineCourseAnnouncements,
  onlineCourseChapters: standardApiRequests,
  onlineCourseFeedbacks,
  onlineCoursePolls,
  onlineCourseQuestionReplies: standardApiRequests,
  onlineCourseQuestions,
  onlineCourses,
  onlineCourseSections: standardApiRequests,
  onlineCourseSurveys,
  onlineCourseUsers,
  partners: standardApiRequests,
  partnerUsers: standardApiRequests,
  platform,
  quizQuestions,
  quizQuestionSubmissions: standardApiRequests,
  quizzes,
  reports,
  resourceReactions,
  resourceViews,
  scholarshipApplications: standardApiRequests,
  scholarships: standardApiRequests,
  schools: standardApiRequests,
  search,
  siteTours,
  stripeProducts: standardApiRequests,
  studentCourseMessages: standardApiRequests,
  studentCourseMessageReplies: standardApiRequests,
  studentExtraCurricularActivities: standardApiRequests,
  studentTestRecords: standardApiRequests,
  studentTutorContracts: standardApiRequests,
  studyBuddyQuestions: standardApiRequests,
  studyBuddyResponses: standardApiRequests,
  studyGroups: standardApiRequests,
  studyGroupUsers: standardApiRequests,
  studyInitiativeMaterials: standardApiRequests,
  studyInitiatives: standardApiRequests,
  studyInitiativeSkills: standardApiRequests,
  studyPlannerItems: standardApiRequests,
  studyPlannerTemplateItems: standardApiRequests,
  studyRoomErrors,
  studyRooms,
  studySessionBundles: standardApiRequests,
  studySessionBundleUsers: standardApiRequests,
  studySessions: standardApiRequests,
  studySessionUsers: standardApiRequests,
  subscriptions: standardApiRequests,
  tokenCredits: standardApiRequests,
  tokenPurchases: standardApiRequests,
  tutorAvailabilities,
  tutorFeedbacks,
  tutoringSessionStudentFeedbacks: standardApiRequests,
  tutoringSessionTutorFeedbacks: standardApiRequests,
  tutorRequests,
  userCourseAuditLogs: standardApiRequests,
  userCourseInstructorNotes: standardApiRequests,
  userCourses: standardApiRequests,
  users,
  votes,
  whiteboards,
  workShifts: standardApiRequests,
  workShiftUsers,
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options
});

const composeEnhancers =
  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ?
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      // Specify extension's options like name, actionsBlacklist, actionsCreators, serialize...
      trace: true,
      traceLimit: 25,
    })
  : compose;

const enhancer = composeEnhancers(
  applyMiddleware(thunk),
  sentryReduxEnhancer,
  // other store enhancers if any
);

export default initialState => {
  window.REDUX_INITIAL_STATE = initialState;
  return createStore(combinedReducerEnhancer(reducer), initialState, enhancer);
};
